import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare, Mail, MessageCircle, Settings as SettingsIcon, FileText, Save, CheckCircle2, AlertTriangle, Copy, Check, Workflow, Send } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import { formatDateTime } from '../utils/datetime';
import BulkEmail from './BulkEmail';
// Custom Email Templates landed here 2026-06-03 (moved out of
// AutomationSection). Operators send these from Compose so the templates
// page and the send page sit side-by-side under one section.
import CustomTemplatesTab from './automation/CustomTemplatesTab';
// Unified SMS inbox — a running log of ALL texts with click-to-reply.
// Replaces the old "Coming soon" Text-tab placeholder. Complements the
// per-customer Text tab (both render the shared <SmsThread/>).
import SmsInbox from '../components/sms/SmsInbox';
import TextCompose from '../components/sms/TextCompose';
import AutomationSection from './AutomationSection';

/**
 * Communication section. Two channels operators interact with tenants
 * over: Email and Text. Each top-level tab is the CHANNEL, not the
 * send-scale -- the picker inside each tab decides bulk-vs-individual
 * (Add one recipient = individual, Add All = bulk). Mirrors how the
 * customer form already separates email vs phone fields.
 *
 * Now lives as a Settings tab (2026-06-01) — render path is
 * /settings?tab=communication. Sub-tab state still rides on ?view=...
 * so legacy bookmarks like ?view=text continue to work after the
 * /communication -> /settings?tab=communication redirect.
 *
 * Sub-tabs:
 *   /settings?tab=communication                          -> Email (default)
 *   /settings?tab=communication&view=text                -> SMS / Text
 *   /settings?tab=communication&view=custom-templates    -> Operator-authored email templates
 *   /settings?tab=communication&view=settings            -> Sender + AIVA key config
 */

// Communication splits by WHO SENDS IT, because that is the question an
// operator actually has: "the system sends this on its own" vs "I send this".
// Automation (rules + their wording + the events they listen to) used to be a
// separate Settings tab, which meant the automated half of tenant comms lived
// nowhere near the manual half.
const MODES = [
  { key: 'automation', label: 'Automation', icon: Workflow,
    desc: 'Emails and texts the system sends on its own — rules, wording, events' },
  { key: 'manual', label: 'Manual', icon: Send,
    desc: 'Messages you send yourself — one tenant or a bulk send' },
  // Logs are their own surfaces. They answer "did it go out?" and "what did
  // the tenant say?" — lookup tasks, not send tasks — and living inside the
  // Manual compose views buried them under a long form.
  { key: 'email-log', label: 'Email log', icon: Mail,
    desc: 'Every email sent from here — who got it, when, and its full contents' },
  { key: 'text-log', label: 'Text log', icon: MessageCircle,
    desc: 'Every text conversation — outbound sends and tenant replies, threaded' },
  // Sender config applies to BOTH automated and manual sends, so it was
  // wrong as a Manual sub-tab — it is not something you "send".
  { key: 'settings', label: 'Email & SMS Settings', icon: SettingsIcon,
    desc: 'Who tenant email and texts come from — sender address, name, reply-to' },
];

// Sub-tabs under Manual. (Automation renders AutomationSection, which brings
// its own sub-nav.) Every tab is built; there is no placeholder branch —
// the old "Coming soon" fallback (with its support mailto) was unreachable
// and was removed 2026-09-02.
const TABS = [
  {
    key: 'email',
    label: 'Email',
    icon: Mail,
    desc: 'Send email to one tenant or many at once',
  },
  {
    key: 'text',
    label: 'Text',
    icon: MessageCircle,
    desc: 'Type a number, type a message, send. Replies land in the Text log.',
  },
  {
    key: 'custom-templates',
    // Holds BOTH channels — the tab has a "New Text Template" button — so
    // naming it "Email" hid half of what lives here.
    label: 'Custom Email & Text Templates',
    icon: FileText,
    desc: 'Operator-authored email and text templates for one-off announcements or bulk sends. They never fire from automation rules.',
  },
];

// Legacy query-string values that map onto the new collapsed tabs. Keeps
// bookmarks + old links from breaking after the 4-to-2 consolidation.
const VIEW_ALIASES = {
  'bulk-email': 'email',
  'customer-email': 'email',
  'bulk-text': 'text',
  'customer-text': 'text',
};


// Module-scope so they aren't redefined on every parent render —
// otherwise React unmounts each input after every keystroke and the
// caret loses focus.
const SettingsField = ({ label, hint, children }) => (
  <div>
    <label className="block text-[12px] font-medium text-slate-700 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);
const SettingsInput = (props) => (
  <input {...props} className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500" />
);

// SMS provider registry. Adding a provider later = one more entry here:
// its dropdown label, the inbound-webhook path the provider console posts
// to, and any extra connection fields the operator must capture. The form
// reads everything off this map so the UI stays provider-agnostic.
//   value          -> stored in facility.sms_provider (lowercase, generic)
//   label          -> dropdown text
//   webhookPath    -> appended to the BACKEND API base for the inbound URL
//   connectionFields -> env-var-NAME fields shown only for this provider
//                       (NAMES, never secret values — secrets live on the host)
const SMS_PROVIDERS = [
  {
    value: 'twilio',
    label: 'Twilio',
    webhookPath: '/api/webhooks/twilio/sms',
    connectionFields: [],
  },
  {
    value: 'quo',
    label: 'Quo (formerly OpenPhone)',
    webhookPath: '/api/webhooks/quo/sms',
    connectionFields: [
      {
        key: 'quo_api_key_env_var',
        label: 'Quo API key — env var name',
        placeholder: 'e.g. MYSMS360_<FACILITY>_QUO_API_KEY',
        hint: 'NAME of the host env var holding the Quo API key — not the key itself. Platform support sets the actual secret on the host. Uppercase letters, digits, underscores only.',
      },
      {
        key: 'quo_signing_secret_env_var',
        label: 'Quo webhook signing secret — env var name',
        placeholder: 'e.g. MYSMS360_<FACILITY>_QUO_SIGNING_SECRET',
        hint: 'NAME of the host env var holding the Quo webhook signing secret — not the secret itself. Leave blank to accept inbound without HMAC verification. Uppercase letters, digits, underscores only.',
      },
    ],
  },
];
const DEFAULT_SMS_PROVIDER = 'twilio';

// Backend API base — the inbound webhook must hit the API host, NOT the
// frontend app domain. Mirrors apiClient's baseURL resolution.
const BACKEND_BASE_URL = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002').replace(/\/+$/, '');

// Read-only, copy-friendly value (webhook URL). Copy button mirrors the
// navigator.clipboard pattern used in GateAccessSection.
const CopyableValue = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-stretch gap-2">
      <input
        type="text"
        readOnly
        value={value}
        onFocus={(e) => e.target.select()}
        style={{ fontFamily: 'monospace' }}
        className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-[12px] bg-slate-50 text-slate-700 focus:outline-none focus:border-blue-500"
      />
      <button
        type="button"
        onClick={copy}
        aria-label="Copy webhook URL"
        className="px-2 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-slate-500" />}
      </button>
    </div>
  );
};


// Per-facility comms settings form. The fields here are read by
// backend/services/notification_service.py:send_template when the
// 13 transactional triggers and the 4 manual composers fire.
// Email delivery tracking posture (GET .../email-delivery/status). The label
// describes NOW — computed from the facility's own rows — never a stored flag.
const TRACKING_LABEL = {
  active: 'Receiving confirmations',
  waiting: 'Waiting for the first confirmation',
  not_receiving: 'Not receiving confirmations',
  stalled: 'Confirmations stopped',
  no_sends: 'No email sent recently',
};
const TRACKING_CLS = {
  active: 'text-emerald-700',
  waiting: 'text-slate-600',
  not_receiving: 'text-amber-700',
  stalled: 'text-amber-700',
  no_sends: 'text-slate-500',
};
const DEFAULT_WEBHOOK_EVENTS = [
  'email.delivered', 'email.bounced', 'email.complained',
  'email.opened', 'email.clicked', 'email.delivery_delayed',
];

const CommsSettingsForm = ({ facility }) => {
  const facilityId = facility?._id || facility?.id;
  const [form, setForm] = useState({
    sender_email: '',
    sender_name: '',
    sender_reply_to: '',
    sender_sms_number: '',
    sender_email_signature: '',
    resend_env_var: '',
    sms_provider: DEFAULT_SMS_PROVIDER,
    quo_api_key_env_var: '',
    quo_signing_secret_env_var: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  // Email delivery tracking (platform feature, 2026-09-01). Delivered /
  // bounced / seen come from the facility's OWN Resend account posting to
  // our webhook, signed with that account's secret. Self-serve: the secret
  // is pasted here, stored encrypted on the facility and never shown again.
  const [delivery, setDelivery] = useState(null);
  const [whSecret, setWhSecret] = useState('');
  const [whBusy, setWhBusy] = useState(false);
  const loadDelivery = useCallback(async () => {
    if (!facilityId) return;
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}/email-delivery/status`);
      setDelivery(r.data || null);
    } catch { setDelivery(null); }
  }, [facilityId]);
  useEffect(() => { loadDelivery(); }, [loadDelivery]);

  const saveSecret = async () => {
    if (!whSecret.trim()) return;
    setWhBusy(true); setError(null);
    try {
      await apiClient.put(`/api/facilities/${facilityId}/email-delivery/webhook-secret`,
        { secret: whSecret.trim() });
      setWhSecret('');
      setInfo('Webhook secret stored.');
      setTimeout(() => setInfo(null), 2500);
      loadDelivery();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setWhBusy(false);
    }
  };
  const clearSecret = async () => {
    setWhBusy(true); setError(null);
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/email-delivery/webhook-secret`);
      loadDelivery();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setWhBusy(false);
    }
  };

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}`);
      const f = r.data || {};
      setForm({
        sender_email: f.sender_email || '',
        sender_name: f.sender_name || '',
        sender_reply_to: f.sender_reply_to || '',
        sender_sms_number: f.sender_sms_number || '',
        sender_email_signature: f.sender_email_signature || '',
        resend_env_var: f.resend_env_var || '',
        // Unset sms_provider behaves as the default ("twilio") server-side.
        sms_provider: f.sms_provider || DEFAULT_SMS_PROVIDER,
        quo_api_key_env_var: f.quo_api_key_env_var || '',
        quo_signing_secret_env_var: f.quo_signing_secret_env_var || '',
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    setSaving(true); setError(null); setInfo(null);
    try {
      // Normalize blank env-var-NAME fields -> null so the backend clears
      // them instead of validating an empty string against the
      // uppercase-letters-digits-underscores regex. Same treatment the
      // backend already gives resend_env_var / stripe_secret_env_var.
      const nn = (v) => (v?.trim() ? v.trim() : null);
      const payload = {
        ...form,
        resend_env_var: nn(form.resend_env_var),
        quo_api_key_env_var: nn(form.quo_api_key_env_var),
        quo_signing_secret_env_var: nn(form.quo_signing_secret_env_var),
      };
      await apiClient.put(`/api/facilities/${facilityId}`, payload);
      setInfo('Saved.');
      setTimeout(() => setInfo(null), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Resolve the selected provider (fall back to the default registry entry
  // if a facility somehow stored an unknown value) so the conditional
  // fields + webhook URL always have something to render.
  const activeProvider =
    SMS_PROVIDERS.find((p) => p.value === form.sms_provider) ||
    SMS_PROVIDERS.find((p) => p.value === DEFAULT_SMS_PROVIDER);

  return (
    <div className="max-w-2xl">
      <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Outbound sender (email + SMS)</h2>
          <p className="text-[12px] text-slate-500 mt-1">
            Tenant-facing emails and texts go out from these values. Leave blank to fall back to the platform defaults (<code>noreply@mysms360.com</code>).
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5" /> {error}
          </div>
        )}
        {info && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5" /> {info}
          </div>
        )}

        <SettingsField label="From email"
               hint="Must be a verified sender on a domain you control (configure DKIM/SPF at resend.com). Until verified, sends fall back to noreply@mysms360.com.">
          <SettingsInput type="email" value={form.sender_email} onChange={set('sender_email')} placeholder="contact@yourstorage.com" disabled={loading} />
        </SettingsField>
        <SettingsField label="From name"
               hint="Display name tenants see in their inbox.">
          <SettingsInput value={form.sender_name} onChange={set('sender_name')} placeholder="Your Self Storage" disabled={loading} />
        </SettingsField>
        <SettingsField label="Reply-to address"
               hint="Where tenant replies route to. Often the same as From email.">
          <SettingsInput type="email" value={form.sender_reply_to} onChange={set('sender_reply_to')} placeholder="contact@yourstorage.com" disabled={loading} />
        </SettingsField>
        <SettingsField label="Text message provider"
               hint="Which provider sends and receives this facility's texts. Default Twilio.">
          <select
            value={form.sms_provider}
            onChange={set('sms_provider')}
            disabled={loading}
            className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-blue-500"
          >
            {SMS_PROVIDERS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </SettingsField>
        <SettingsField label="SMS sender number"
               hint="Your texting number in E.164 (+15551234567), 10DLC-registered.">
          <SettingsInput value={form.sender_sms_number} onChange={set('sender_sms_number')} placeholder="+15551234567" disabled={loading} />
        </SettingsField>

        {/* Provider-specific connection fields. Driven off the registry so a
            new provider's fields appear automatically. These are env-var
            NAMES, never secret values. */}
        {activeProvider.connectionFields.map((cf) => (
          <SettingsField key={cf.key} label={cf.label} hint={cf.hint}>
            <input
              type="text"
              value={form[cf.key]}
              onChange={set(cf.key)}
              placeholder={cf.placeholder}
              disabled={loading}
              style={{ fontFamily: 'monospace' }}
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
            />
          </SettingsField>
        ))}

        {/* Inbound webhook URL to paste into the provider console. Uses the
            BACKEND API base (not the frontend app domain) — the provider
            must POST replies to the API host. Switches by provider. */}
        <SettingsField label="Inbound webhook URL"
               hint={`Paste this into your ${activeProvider.label} console so tenant replies reach mySMS360. Points at the API host.`}>
          <CopyableValue value={`${BACKEND_BASE_URL}${activeProvider.webhookPath}`} />
        </SettingsField>
        <SettingsField label="Email signature"
               hint="HTML appended below the body of every transactional + manual email. Address, hours, opt-out link, etc.">
          <textarea value={form.sender_email_signature} onChange={set('sender_email_signature')}
                    rows={4}
                    placeholder="Your Self Storage&#10;123 Main St, Your City, ST 00000&#10;(000) ###-####"
                    disabled={loading}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500" />
        </SettingsField>
        <SettingsField label="Resend env var name (advanced, platform-managed)"
               hint="Names the Railway environment variable that holds this facility's Resend API key. Leave blank to use the platform default. Format: uppercase letters, digits, underscores only. Contact mySMS360 platform support to provision the env var before setting this.">
          <input type="text"
                 value={form.resend_env_var}
                 onChange={set('resend_env_var')}
                 placeholder="e.g. RESEND_YOURFACILITY"
                 disabled={loading}
                 style={{fontFamily: 'monospace'}}
                 className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500" />
        </SettingsField>

        {/* Email delivery tracking — self-serve webhook wiring + live posture. */}
        <div className="rounded-lg border border-slate-200 p-3 space-y-3">
          <div>
            <p className="text-[12px] font-medium text-slate-700">Email delivery tracking</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Delivered, bounced and seen come from your Resend account&apos;s webhook. Add the URL below
              as a webhook in Resend with the listed events, then paste its signing secret here.
            </p>
          </div>
          {delivery && (
            <p className={`text-[12px] ${TRACKING_CLS[delivery.tracking] || 'text-slate-600'}`}>
              <strong>{TRACKING_LABEL[delivery.tracking] || delivery.tracking}.</strong> {delivery.detail}
            </p>
          )}
          <SettingsField label="Webhook URL"
                 hint={`Events: ${(delivery?.events || DEFAULT_WEBHOOK_EVENTS).join(', ')}`}>
            <CopyableValue value={`${BACKEND_BASE_URL}${delivery?.webhook_path || '/api/webhooks/resend'}`} />
          </SettingsField>
          <SettingsField label="Webhook signing secret"
                 hint={delivery?.secret_source === 'facility'
                   ? `Stored encrypted${delivery.secret_set_at ? ` on ${formatDateTime(delivery.secret_set_at, facility?.timezone)}` : ''}. Paste a new one to replace it.`
                   : delivery?.secret_source === 'env'
                   ? 'Provided by the platform host for this facility. Paste one here to manage it yourself.'
                   : 'Stored encrypted and never shown again. Without it, events from your account are rejected.'}>
            <div className="flex gap-2">
              <input type="password" value={whSecret} onChange={(e) => setWhSecret(e.target.value)}
                     placeholder={delivery?.secret_source === 'facility' ? '•••• stored' : 'whsec_…'}
                     disabled={whBusy} autoComplete="off"
                     style={{ fontFamily: 'monospace' }}
                     className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500" />
              <button type="button" onClick={saveSecret} disabled={whBusy || !whSecret.trim()}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[12px] font-medium whitespace-nowrap">
                Save secret
              </button>
              {delivery?.secret_source === 'facility' && (
                <button type="button" onClick={clearSecret} disabled={whBusy}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded-md text-[12px] font-medium whitespace-nowrap">
                  Clear
                </button>
              )}
            </div>
          </SettingsField>
          {delivery && delivery.tracking === 'active' && delivery.open_tracking === 'never_seen' && (
            <p className="text-[11px] text-slate-400">
              Delivery is confirmed, but no email has ever been reported as seen. Turn on Open tracking
              for the sending domain in Resend and &quot;Seen&quot; starts appearing.
            </p>
          )}
        </div>

        <div className="pt-2 flex justify-end">
          <button onClick={onSave} disabled={saving || loading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium">
            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 mt-4 max-w-2xl">
        <p className="text-[12px] text-amber-800">
          <strong>Domain verification:</strong> Resend will refuse to send from a domain it hasn't verified.
          Set up DKIM + SPF records for your domain at <a className="underline" href="https://resend.com/domains" target="_blank" rel="noopener noreferrer">resend.com/domains</a> before changing the from-email.
        </p>
      </div>
    </div>
  );
};

// NOTE: VoiceAgentKeyCard moved to frontend/src/components/aiva/VoiceAgentKeyCard.js
// on 2026-06-01. It now renders on the AIVA page (/intelligence Settings tab)
// because it's AIVA-specific config, not comms-channel config.


// How often we refetch the unread-text count so a freshly-arrived tenant
// reply bumps the Text-tab badge without a manual refresh. Matches the
// inbox/thread poll cadence.
const UNREAD_POLL_MS = 25_000;

const CommunicationSection = ({ embedded = false }) => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id || currentFacility?.id || '';
  const [searchParams, setSearchParams] = useSearchParams();

  // Unread inbound-text count -> badge on the Text tab. Polled, and
  // refetched on demand (via refreshUnread) right after the inbox marks a
  // conversation read so the badge drops immediately.
  const [unreadCount, setUnreadCount] = useState(0);
  const refreshUnread = useCallback(async () => {
    if (!facilityId) return;
    try {
      const { data } = await apiClient.get(
        `/api/facilities/${facilityId}/sms/unread-count`,
      );
      setUnreadCount(typeof data?.unread_count === 'number' ? data.unread_count : 0);
    } catch {
      // Non-fatal — the badge just won't update this cycle.
    }
  }, [facilityId]);

  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, UNREAD_POLL_MS);
    return () => clearInterval(id);
  }, [refreshUnread]);
  // Map legacy 4-tab keys (bulk-email, customer-email, bulk-text, customer-text)
  // onto the new 2-tab keys (email, text). Default lands on Email.
  let rawView = searchParams.get('view');
  // Sender config was a Manual sub-tab before it became its own mode; keep
  // old ?view=settings bookmarks working.
  if (rawView === 'settings' && !searchParams.get('mode')) rawView = null;
  const resolved = VIEW_ALIASES[rawView] || rawView;
  // ?mode=automation renders the rules surface. A bare visit lands on the
  // Email log (owner default, 2026-08-06 — the log is what operators check;
  // composing is the rarer act). Existing ?view= deep links keep working
  // and still imply Manual, since view keys are Manual sub-tabs.
  const rawMode = searchParams.get('mode');
  const modeFallback = resolved ? 'manual' : 'email-log';
  const mode = MODES.find((m) => m.key === rawMode) ? rawMode : modeFallback;
  const activeKey = resolved && TABS.find((t) => t.key === resolved) ? resolved : 'email';
  const activeTab = TABS.find((t) => t.key === activeKey) || TABS[0];

  const setMode = (key) => {
    const params = new URLSearchParams(searchParams);
    // email-log is the param-less default state now.
    if (key === 'email-log') params.delete('mode');
    else params.set('mode', key);
    // Leaving a mode drops the other mode's sub-tab so a stale ?view= from
    // Manual doesn't follow you into Automation.
    params.delete('view');
    setSearchParams(params, { replace: true });
  };

  const setTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'email') params.delete('view');
    else params.set('view', key);
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      {!embedded && (
        <div className="mb-5">
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-slate-400" />
            Communication
          </h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            {activeTab.desc}
            {currentFacility?.name && <> &middot; {currentFacility.name}</>}
          </p>
        </div>
      )}
      {embedded && (
        <p className="text-[12px] text-slate-400 mb-4">
          {activeTab.desc}
          {currentFacility?.name && <> &middot; {currentFacility.name}</>}
        </p>
      )}

      {/* Mode strip — Automation vs Manual */}
      <div className="border-b border-slate-200 mb-4">
        <nav className="-mb-px flex gap-6" aria-label="Communication mode">
          {MODES.map((m) => {
            const active = mode === m.key;
            const MIcon = m.icon;
            // Unread badge lives on Text LOG (the inbox where replies
            // land) — it sat on Manual's compose tab, which is not
            // where anyone reads messages.
            const showModeBadge = m.key === 'text-log' && unreadCount > 0;
            return (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-semibold transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <MIcon className="h-3.5 w-3.5" />
                {m.label}
                {showModeBadge && (
                  <span
                    aria-label={`${unreadCount} unread text${unreadCount === 1 ? '' : 's'}`}
                    className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-blue-600 text-white text-[10.5px] font-semibold leading-none"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {mode === 'automation' && <AutomationSection embedded />}

      {mode === 'email-log' && <BulkEmail facility={currentFacility} view="log" />}

      {mode === 'text-log' && (
        <SmsInbox facility={currentFacility} onReadChange={refreshUnread} />
      )}

      {mode === 'settings' && <CommsSettingsForm facility={currentFacility} />}

      {mode === 'manual' && (
      <>
      <div className="border-b border-slate-200 mb-5">
        <nav className="-mb-px flex gap-6" aria-label="Communication tabs">
          {TABS.map((t) => {
            const active = activeKey === t.key;
            const Icon = t.icon;
            const showBadge = false; // unread badge moved to the Text log mode tab
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {showBadge && (
                  <span
                    aria-label={`${unreadCount} unread text${unreadCount === 1 ? '' : 's'}`}
                    className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-blue-600 text-white text-[10.5px] font-semibold leading-none"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {activeKey === 'email' ? (
        <BulkEmail facility={currentFacility} view="compose" />
      ) : activeKey === 'custom-templates' ? (
        <CustomTemplatesTab
          facilityId={currentFacility?._id || currentFacility?.id || ''}
          facility={currentFacility}
        />
      ) : activeKey === 'text' ? (
        <TextCompose facility={currentFacility} />
      ) : null}
      </>
      )}
    </div>
  );
};

export default CommunicationSection;
